import { useState, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Link as LinkIcon, CheckCircle, Loader2, Star, Award, Users, Briefcase,
  Instagram, Globe, Phone, Mail, MapPin, Building, User, Upload, X, Camera
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { fpReferralsAPI } from '../lib/api';

const CATEGORIES = [
  { value: 'real_estate', label: 'Real Estate Agent / Broker' },
  { value: 'financial', label: 'Financial Advisor' },
  { value: 'legal', label: 'Attorney / Legal Services' },
  { value: 'healthcare', label: 'Healthcare Provider' },
  { value: 'therapist', label: 'Therapist / Counselor' },
  { value: 'insurance', label: 'Insurance Agent' },
  { value: 'career_coach', label: 'Career Coach' },
  { value: 'tax', label: 'Tax Preparer / CPA' },
  { value: 'tech', label: 'Tech Consultant' },
  { value: 'photographer', label: 'Photographer' },
  { value: 'event_planner', label: 'Event Planner' },
  { value: 'personal_trainer', label: 'Personal Trainer / Fitness' },
  { value: 'graphic_designer', label: 'Graphic Designer' },
  { value: 'web_developer', label: 'Web Developer' },
  { value: 'hair_stylist', label: 'Hair Stylist / Barber' },
  { value: 'makeup_artist', label: 'Makeup Artist' },
  { value: 'caterer', label: 'Caterer / Chef' },
  { value: 'tutor', label: 'Tutor / Educator' },
  { value: 'travel', label: 'Travel Agent' },
  { value: 'other', label: 'Other' },
];

const BENEFITS = [
  { icon: Award, title: 'Founding Provider Badge', desc: 'Permanent badge on your listing that sets you apart' },
  { icon: Star, title: '6 Months Featured', desc: 'Priority placement in your city and category' },
  { icon: Users, title: 'HBCU Alumni Audience', desc: 'Direct access to a vetted, engaged community' },
  { icon: Briefcase, title: 'We Build It For You', desc: 'Send us your info — we handle setup end-to-end' },
];

const FoundingProviderApplication = () => {
  const [form, setForm] = useState({
    name: '',
    business_name: '',
    category: '',
    business_description: '',
    location: '',
    contact_phone: '',
    contact_email: '',
    website_url: '',
    headshot_url: '',
    instagram_url: '',
    facebook_url: '',
    tiktok_url: '',
    is_hbcu_alum: false,
    hbcu_name: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [prefilled, setPrefilled] = useState(false);
  const [referral, setReferral] = useState(null);
  const fileInputRef = useRef(null);
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const referralToken = searchParams.get('ref');

  // Look up referral by token so we can prefill + show an attribution banner.
  useEffect(() => {
    if (!referralToken) return;
    let cancelled = false;
    fpReferralsAPI.lookup(referralToken).then(res => {
      if (cancelled) return;
      const r = res.data || {};
      setReferral(r);
      setForm(prev => ({
        ...prev,
        name: prev.name || r.prospect_name || '',
        contact_email: prev.contact_email || r.prospect_email || '',
        business_name: prev.business_name || r.prospect_business || '',
      }));
    }).catch(() => {
      // Invalid/expired token — silently ignore, user can still fill the form normally.
    });
    return () => { cancelled = true; };
  }, [referralToken]);

  useEffect(() => {
    if (!user || prefilled) return;
    setForm(prev => ({
      ...prev,
      name: prev.name || user.name || '',
      contact_email: prev.contact_email || user.email || '',
      contact_phone: prev.contact_phone || user.phone || '',
      location: prev.location || user.current_location || user.hometown || '',
      website_url: prev.website_url || user.website || '',
      headshot_url: prev.headshot_url || user.profile_photo || '',
      instagram_url: prev.instagram_url || user.instagram_url || '',
      facebook_url: prev.facebook_url || user.facebook_url || '',
      tiktok_url: prev.tiktok_url || user.tiktok_url || '',
      is_hbcu_alum: prev.is_hbcu_alum || Boolean(user.institution),
      hbcu_name: prev.hbcu_name || user.institution || '',
    }));
    if (user.profile_photo && !photoPreview) setPhotoPreview(user.profile_photo);
    setPrefilled(true);
  }, [user, prefilled, photoPreview]);

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file.'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB.'); return; }
    setPhotoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/founding-provider-headshot`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      setForm(prev => ({ ...prev, headshot_url: data.url }));
      setPhotoPreview(data.url);
      toast.success('Photo uploaded!');
    } catch (err) {
      toast.error(err.message || 'Upload failed. Try pasting a URL instead.');
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contact_email.trim()) {
      toast.error('Name and email are required.');
      return;
    }
    if (!form.category) {
      toast.error('Please select your service category.');
      return;
    }
    setLoading(true);
    try {
      const payload = referralToken ? { ...form, referral_token: referralToken } : form;
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/provider-applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Submission failed');
      setSubmitted(true);
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    const signupHref = `/register?email=${encodeURIComponent(form.contact_email)}&name=${encodeURIComponent(form.name)}`;
    return (
      <div className="min-h-screen bg-gradient-to-br from-dark-slate via-slate-800 to-dark-slate flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl p-10 max-w-md w-full text-center shadow-2xl">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-dark-slate mb-3">You're Live on GradLink!</h2>
          <p className="text-slate-gray mb-2">
            Welcome, <strong>{form.name}</strong>! Your listing is now live and visible to the GradLink community.
          </p>
          <p className="text-slate-gray text-sm mb-6">
            Our team will review your listing and may reach out at <strong>{form.contact_email}</strong> if we have any questions.
          </p>

          <div className="bg-mint-fresh/10 border border-mint-fresh/30 rounded-2xl p-5 mb-6 text-left">
            <p className="font-semibold text-dark-slate text-sm mb-1">Want to manage your listing yourself?</p>
            <p className="text-slate-gray text-xs leading-relaxed mb-3">
              Create a free GradLink account to edit your listing, message clients, and connect with the community.
              You don't need to be an HBCU alum to join — providers, sponsors, and community members are all welcome.
            </p>
            <Link to={signupHref}>
              <Button className="w-full bg-mint-fresh hover:bg-mint-fresh/90 text-dark-slate rounded-xl h-11 font-semibold" data-testid="founding-provider-signup-cta">
                Create my GradLink account →
              </Button>
            </Link>
          </div>

          <div className="space-y-3">
            <Link to="/login">
              <Button variant="outline" className="w-full rounded-xl h-12 border-slate-200">
                Maybe later — explore GradLink
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-slate via-slate-800 to-dark-slate">
      {/* Header */}
      <header className="px-6 py-5 flex items-center justify-between max-w-6xl mx-auto">
        <Link to="/login" className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-mint-fresh/20 rounded-xl flex items-center justify-center">
            <LinkIcon className="w-6 h-6 text-mint-fresh" />
          </div>
          <span className="font-heading font-extrabold text-xl text-white">GradLink</span>
        </Link>
        <Link to="/login" className="text-white/60 hover:text-white text-sm transition-colors">
          Already a member? Sign in
        </Link>
      </header>

      <div className="max-w-6xl mx-auto px-6 pb-20">
        {/* Hero */}
        <div className="text-center pt-10 pb-14">
          <div className="inline-flex items-center space-x-2 bg-mint-fresh/20 text-mint-fresh text-sm font-semibold px-4 py-2 rounded-full mb-6">
            <Star className="w-4 h-4" />
            <span>Founding Provider Program — Limited Spots</span>
          </div>
          <h1 className="font-heading font-extrabold text-4xl md:text-5xl text-white mb-4 leading-tight">
            Reach 1,000+ HBCU Alumni<br />
            <span className="text-mint-fresh">Ready to Hire You</span>
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto leading-relaxed">
            GradLink is the trusted network where HBCU graduates find the professionals they need.
            Get listed as a Founding Provider — at no cost during the founding window.
          </p>
        </div>

        {/* Benefits bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {BENEFITS.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 text-center border border-white/10">
              <Icon className="w-7 h-7 text-mint-fresh mx-auto mb-3" />
              <p className="font-semibold text-white text-sm mb-1">{title}</p>
              <p className="text-white/50 text-xs leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>

        {/* Form */}
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl">
          <h2 className="font-heading font-bold text-2xl text-dark-slate mb-2">Apply Now</h2>
          <p className="text-slate-gray mb-6 text-sm">No account required. Takes about 3 minutes.</p>

          {referral && !referral.expired && (
            <div className="mb-6 rounded-2xl border border-mint-fresh/40 bg-mint-fresh/10 p-5">
              <div className="flex items-start gap-3">
                <Award className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-dark-slate">
                    You were referred by {referral.referrer_name}
                    {referral.referrer_institution ? ` (${referral.referrer_institution})` : ''}
                  </p>
                  {referral.note && (
                    <p className="text-sm text-slate-gray mt-2 italic leading-relaxed">"{referral.note}"</p>
                  )}
                  <p className="text-xs text-slate-gray mt-2">We've pre-filled some fields below — edit anything that needs updating.</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name + Business Name */}
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label className="text-dark-slate font-semibold text-sm mb-2 block flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5" /> Your Name <span className="text-red-500">*</span>
                </Label>
                <Input name="name" value={form.name} onChange={handleChange} placeholder="First and last name"
                  className="h-12 rounded-xl border-slate-200" required />
              </div>
              <div>
                <Label className="text-dark-slate font-semibold text-sm mb-2 block flex items-center gap-1.5">
                  <Building className="w-3.5 h-3.5" /> Business Name
                </Label>
                <Input name="business_name" value={form.business_name} onChange={handleChange} placeholder="Your business or brand name"
                  className="h-12 rounded-xl border-slate-200" />
              </div>
            </div>

            {/* Category */}
            <div>
              <Label className="text-dark-slate font-semibold text-sm mb-2 block flex items-center gap-1.5">
                <Briefcase className="w-3.5 h-3.5" /> Service Category <span className="text-red-500">*</span>
              </Label>
              <select name="category" value={form.category} onChange={handleChange}
                className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white text-dark-slate text-sm focus:outline-none focus:ring-2 focus:ring-dark-slate/20"
                required>
                <option value="">Select your industry...</option>
                {CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div>
              <Label className="text-dark-slate font-semibold text-sm mb-2 block">
                Tell us about your business <span className="text-red-500">*</span>
              </Label>
              <Textarea name="business_description" value={form.business_description} onChange={handleChange}
                placeholder="What you do, who you serve, how long you've been in business, any notable achievements..."
                className="rounded-xl border-slate-200 min-h-[120px] resize-none" required />
            </div>

            {/* Location + Phone */}
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label className="text-dark-slate font-semibold text-sm mb-2 block flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" /> City, State
                </Label>
                <Input name="location" value={form.location} onChange={handleChange} placeholder="City, ST"
                  className="h-12 rounded-xl border-slate-200" />
              </div>
              <div>
                <Label className="text-dark-slate font-semibold text-sm mb-2 block flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5" /> Phone Number
                </Label>
                <Input name="contact_phone" value={form.contact_phone} onChange={handleChange} placeholder="(555) 000-0000"
                  className="h-12 rounded-xl border-slate-200" />
              </div>
            </div>

            {/* Email + Website */}
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label className="text-dark-slate font-semibold text-sm mb-2 block flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" /> Email Address <span className="text-red-500">*</span>
                </Label>
                <Input name="contact_email" type="email" value={form.contact_email} onChange={handleChange}
                  placeholder="you@yourbusiness.com" className="h-12 rounded-xl border-slate-200" required />
              </div>
              <div>
                <Label className="text-dark-slate font-semibold text-sm mb-2 block flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5" /> Website URL
                </Label>
                <Input name="website_url" value={form.website_url} onChange={handleChange} placeholder="https://yourwebsite.com"
                  className="h-12 rounded-xl border-slate-200" />
              </div>
            </div>

            {/* Headshot Upload */}
            <div>
              <Label className="text-dark-slate font-semibold text-sm mb-2 block flex items-center gap-1.5">
                <Camera className="w-3.5 h-3.5" /> Professional Headshot
                <span className="text-slate-400 font-normal ml-2 text-xs">(optional)</span>
              </Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => handlePhotoUpload(e.target.files?.[0])}
              />
              {photoPreview || form.headshot_url ? (
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <img
                    src={photoPreview || form.headshot_url}
                    alt="Headshot preview"
                    className="w-16 h-16 rounded-full object-cover border-2 border-white shadow"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-dark-slate">Photo ready</p>
                    <p className="text-xs text-slate-gray truncate">{form.headshot_url}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setPhotoPreview(null); setForm(prev => ({ ...prev, headshot_url: '' })); }}
                    className="p-1.5 rounded-full hover:bg-slate-200 text-slate-400"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); handlePhotoUpload(e.dataTransfer.files?.[0]); }}
                  className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center cursor-pointer hover:border-dark-slate/40 hover:bg-slate-50 transition-all"
                >
                  {photoUploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                      <p className="text-sm text-slate-gray">Uploading...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-6 h-6 text-slate-400" />
                      <p className="text-sm font-medium text-dark-slate">Click or drag to upload</p>
                      <p className="text-xs text-slate-400">JPG, PNG, WebP — max 5MB</p>
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-slate-400 mt-2">
                Or paste a URL instead:{' '}
                <button
                  type="button"
                  className="underline hover:text-slate-600"
                  onClick={() => {
                    const url = window.prompt('Paste your headshot URL:');
                    if (url) { setForm(prev => ({ ...prev, headshot_url: url })); setPhotoPreview(url); }
                  }}
                >
                  enter URL
                </button>
              </p>
            </div>

            {/* Social links */}
            <div>
              <Label className="text-dark-slate font-semibold text-sm mb-3 block flex items-center gap-1.5">
                <Instagram className="w-3.5 h-3.5" /> Social Media
                <span className="text-slate-400 font-normal ml-2 text-xs">(paste any that apply)</span>
              </Label>
              <div className="grid md:grid-cols-3 gap-4">
                <Input name="instagram_url" value={form.instagram_url} onChange={handleChange}
                  placeholder="Instagram URL" className="h-11 rounded-xl border-slate-200 text-sm" />
                <Input name="facebook_url" value={form.facebook_url} onChange={handleChange}
                  placeholder="Facebook URL" className="h-11 rounded-xl border-slate-200 text-sm" />
                <Input name="tiktok_url" value={form.tiktok_url} onChange={handleChange}
                  placeholder="TikTok URL" className="h-11 rounded-xl border-slate-200 text-sm" />
              </div>
            </div>

            {/* HBCU alum toggle */}
            <div className="bg-emerald-50 rounded-2xl p-5 border border-emerald-100">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" name="is_hbcu_alum" checked={form.is_hbcu_alum} onChange={handleChange}
                  className="mt-0.5 w-5 h-5 rounded border-emerald-300 text-emerald-600 cursor-pointer" />
                <div>
                  <p className="text-dark-slate font-semibold text-sm">I am an HBCU alumnus/alumna</p>
                  <p className="text-slate-gray text-xs mt-0.5">We'll display an HBCU Alum badge on your listing — a strong trust signal for the community.</p>
                </div>
              </label>
              {form.is_hbcu_alum && (
                <div className="mt-4 pl-8">
                  <Input name="hbcu_name" value={form.hbcu_name} onChange={handleChange}
                    placeholder="Which HBCU(s)? e.g. Spelman College, Howard University"
                    className="h-11 rounded-xl border-emerald-200 bg-white text-sm" />
                </div>
              )}
            </div>

            {/* Anything else */}
            <div>
              <Label className="text-dark-slate font-semibold text-sm mb-2 block">
                Anything else we should know?
                <span className="text-slate-400 font-normal ml-2 text-xs">(optional)</span>
              </Label>
              <Textarea name="notes" value={form.notes} onChange={handleChange}
                placeholder="Awards, certifications, years in business, preferred contact time..."
                className="rounded-xl border-slate-200 min-h-[80px] resize-none text-sm" />
            </div>

            {/* Submit */}
            <Button type="submit" disabled={loading}
              className="w-full h-14 bg-dark-slate hover:bg-slate-700 text-white rounded-2xl text-base font-semibold shadow-lg transition-all">
              {loading ? (
                <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</span>
              ) : (
                'Submit My Application →'
              )}
            </Button>

            <p className="text-center text-slate-400 text-xs">
              By submitting, you agree to let GradLink create a public listing on your behalf.
              We will never share your info with third parties.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FoundingProviderApplication;
